import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import {
    DocumentCommandBarItemProps,
    DocumentSelectionType,
    overflowButtonStyles,
    visibleButtonStyles,
} from './DocumentCommandBar'
import { DocumentContext, IDefaultFolderDto, IDocumentStackItemDto, isFolder } from '../../../config/documentConfig'
import { CommandBarButton } from '@fluentui/react'
import OfficeDocumentDialog from '../../Dialog/OfficeDocument/OfficeDocumentDialog'
import DialogButton from '../../Button/DialogButton'
import { DocumentType } from '../../../helpers/documentHelper'
import { useSettings } from '../../../hooks/useSettings'
import { useTranslation } from 'react-i18next'

type Props = DocumentCommandBarItemProps & {
    name: string
}

export default function OfficeDocumentButton(props: Props): ReactElement {
    const settings = useSettings()
    const selectedFolder = props.selectedItems.find(isFolder)
    const { t: translate } = useTranslation()

    const disabled = useMemo(() => {
        const isExcluded =
            selectedFolder &&
            (settings?.folders || [])
                .filter((folder: IDefaultFolderDto) => folder.excluded)
                .map((folder: IDefaultFolderDto) => folder.label.toLowerCase())
                .find((search: string) => selectedFolder.name.toLowerCase().indexOf(search) !== -1)

        return props.isLoading || !!isExcluded
    }, [props.isLoading, settings?.folders, selectedFolder])

    const buttonText = useMemo(() => {
        if (disabled === !props.item.renderedInOverflow) {
            return undefined
        }
        return translate(`file.pivot.documents.create.${props.name.toLowerCase()}.button`)
    }, [props.selectionType, props.item, props.name, disabled])

    return (
        <DialogButton
            button={CommandBarButton}
            buttonProps={{
                text: buttonText,
                disabled,
                iconProps: { iconName: props.name === DocumentType.PDF ? 'PDF' : `${props.name}Document` },
                styles: props.item.renderedInOverflow ? overflowButtonStyles : visibleButtonStyles,
            }}
            dialog={OfficeDocumentDialog}
            dialogProps={{
                dialogContentProps: {
                    title: translate(`file.pivot.documents.create.${props.name.toLowerCase()}.title`),
                },
                context: DocumentContext.Document,
                documentType: DocumentType[props.name],
                selectedFolder,
                folders: props.rootFolder.data,
                reset: (stack?: IDocumentStackItemDto[]) => props.reset(props.sortOrder, stack),
                variables: { file: props.file },
            }}
            tooltipProps={
                props.selectionType === DocumentSelectionType.Folder && !props.item.renderedInOverflow
                    ? { content: translate(`file.pivot.documents.create.${props.name.toLowerCase()}.tooltip`) }
                    : undefined
            }
        />
    )
}
